import { useMutation } from '@apollo/client';
import { ErrorMessage } from '@retainerclub/common';
import { DISCOUNT_CHECK_ACTION } from '@retainerclub/customer-api';
import { useCart } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useState } from 'react';

function DiscountCodeInput({ disabled = false }) {
  const { refetchCart, cartId } = useCart();
  const [discountCode, setDiscountCode] = useState('');
  const [error, setError] = useState(null);
  const [isApplying, setIsApplying] = useState(false);

  const [applyDiscount] = useMutation(DISCOUNT_CHECK_ACTION, {
    onCompleted: (data) => {
      setIsApplying(false);
      if (data?.discountCheckAction?.errorMessages?.length > 0) {
        setError(
          data.discountCheckAction.errorMessages
            .map((err) => err.message)
            .join(', '),
        );
      } else {
        setError(null);
        setDiscountCode('');
        refetchCart();
      }
    },
    onError: (err) => {
      setIsApplying(false);
      setError(err.message || 'Failed to apply discount code');
    },
  });

  function handleApplyDiscount() {
    if (disabled) {
      return;
    }

    if (!discountCode.trim()) {
      setError('Please enter a discount code');
      return;
    }

    if (!cartId) {
      setError('Cart not found');
      return;
    }

    setError(null);
    setIsApplying(true);

    applyDiscount({
      variables: {
        input: {
          input: {
            cartId: cartId,
            code: discountCode.trim(),
          },
          clientMutationId: crypto.randomUUID(),
        },
      },
    });
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApplyDiscount();
    }
  }

  return (
    <div className="col-span-full">
      <label
        htmlFor="discount-code"
        className="block font-medium text-gray-900 text-sm"
      >
        Discount code
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id="discount-code"
          type="text"
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter discount code"
          disabled={disabled || isApplying}
          className="block flex-1 rounded-md border-0 py-1.5 pl-2 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-rcprimary-400 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm sm:leading-6"
        />
        <button
          type="button"
          onClick={handleApplyDiscount}
          disabled={disabled || isApplying || !discountCode.trim()}
          className="inline-flex items-center rounded-md bg-rcprimary-400 px-3 py-1.5 font-semibold text-sm text-white shadow-xs hover:bg-rcprimary-300 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isApplying ? 'Applying...' : 'Apply'}
        </button>
      </div>
      {error && (
        <div className="mt-2">
          <ErrorMessage error={{ message: error }} />
        </div>
      )}
    </div>
  );
}

DiscountCodeInput.propTypes = {
  disabled: PropTypes.bool,
};

export default DiscountCodeInput;
